'use client';

import { SubscriptionForm } from '@/layers/presentation/components/ui/SubscriptionForm';
import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import { useThrowNotification } from '@/layers/presentation/hooks';
import logger from '@/layers/support/monitoring/logger';
import { NotificationType } from '@/types/modelEnums';
import { SubscriptionParams, SubscriptionText } from '@/types/users';
import { Button, Modal, ModalBody, ModalContent, useDisclosure } from '@nextui-org/react';
import React, { createContext, useContext, useState } from 'react';

const LOG_MODULE = 'FE [Presentation / Components / SubscriptionPrompt]';

// Create context for the subscription modal
type SubscriptionModalContextType = {
  openModal: (modal: SubscriptionText, params: SubscriptionParams) => void;
  closeModal: () => void;
};

const SubscriptionModalContext = createContext<SubscriptionModalContextType | null>(null);

// Provider component
export const SubscriptionModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const throwNotification = useThrowNotification();
  const [modalState, setModalState] = useState<{
    modal: SubscriptionText;
    params: SubscriptionParams;
  } | null>(null);

  const openModal = React.useCallback(
    (modal: SubscriptionText, params: SubscriptionParams) => {
      setModalState({ modal, params });
      onOpen();
    },
    [onOpen],
  );

  const closeModal = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubscribeSuccess = React.useCallback(() => {
    throwNotification(NotificationType.Success, 'Done! Please check your email inbox.');
    setTimeout(() => {
      onClose();
    }, 1000);
    logger.debug(`${LOG_MODULE} onSubscribeSuccess`);
  }, [throwNotification, onClose]);

  // This component will never re-render based on parent components
  const ModalComponent = (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      classNames={{
        base: 'rounded-2xl',
        body: 'p-10',
      }}
    >
      {modalState && (
        <ModalContent>
          <ModalBody>
            <SubscriptionForm
              text={modalState.modal}
              params={modalState.params}
              onSubscribeSuccess={handleSubscribeSuccess}
            />
          </ModalBody>
        </ModalContent>
      )}
    </Modal>
  );

  return (
    <SubscriptionModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {ModalComponent}
    </SubscriptionModalContext.Provider>
  );
};

// Hook to use the subscription modal
export const useSubscriptionModal = () => {
  const context = useContext(SubscriptionModalContext);
  if (!context) {
    throw new Error('useSubscriptionModal must be used within a SubscriptionModalProvider');
  }
  return context;
};

// Original component for backwards compatibility
interface SubscriptionPromptProps {
  prompt: SubscriptionText;
  modal: SubscriptionText;
  params: SubscriptionParams;
  background?: string;
}

const SubscriptionPrompt: React.FC<SubscriptionPromptProps> = ({
  prompt,
  modal,
  params,
  background = 'bg-secondary',
}) => {
  const { user } = useGlobalState();
  const { openModal } = useSubscriptionModal();

  if (user) {
    return null;
  }

  return (
    <section className={`w-full ${background} py-12`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center">
          <h3 className="text-2xl sm:text-3xl font-semibold mb-3 max-w-[90%] sm:max-w-full">
            {prompt.title}
          </h3>
          <p className="text-lg sm:text-xl text-gray-600 mb-6 max-w-2xl">{prompt.text}</p>
          <Button
            size="lg"
            radius="full"
            color="primary"
            className="text-white px-8 py-6 text-lg"
            onPress={() => openModal(modal, params)}
          >
            {prompt.button}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SubscriptionPrompt;
